import { parseSearchIntent, type SearchIntent } from "./explorer/searchIntent";

// Lightweight Algolia search client for the shared `af_search` index.
// Uses the public search-only key (safe to ship in the bundle) via the REST API,
// so no SDK dependency is needed. The index is populated by the `af-search-indexer`
// Supabase edge function and holds jobs posted in the last 60 days.

const ALGOLIA_APP_ID = "4W6YIYDU0Y";
const ALGOLIA_SEARCH_KEY = "ee9fd659d98f5e2841f76371023c5fcb";
const ALGOLIA_INDEX = "af_search";

const ALGOLIA_ENDPOINT = `https://${ALGOLIA_APP_ID}-dsn.algolia.net/1/indexes/${ALGOLIA_INDEX}/query`;

async function algoliaQuery(body: Record<string, unknown>) {
  const res = await fetch(ALGOLIA_ENDPOINT, {
    method: "POST",
    headers: {
      "X-Algolia-API-Key": ALGOLIA_SEARCH_KEY,
      "X-Algolia-Application-Id": ALGOLIA_APP_ID,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Algolia search failed: ${res.status}`);
  return res.json();
}

export interface AlgoliaJobHit {
  objectID: string;
  title: string;
  company: string | null;
  location: string | null;
  city: string | null;
  region: string | null;
  metro: string | null;
  country: string | null;
  role: string | null;
  seniority: string | null;
  salary: string | null;
  salary_min: number | null;
  salary_max: number | null;
  is_remote: boolean;
  hours: string | null;
  logo: string | null;
  featured: boolean;
  posted: number; // unix seconds
  url: string;
  _highlightResult?: {
    title?: { value: string; matchLevel: string };
  };
  _rankingInfo?: {
    nbTypos: number;
    nbExactWords: number;
    words: number;
  };
}

export interface AlgoliaJobSearchParams {
  query: string;
  location?: string;
  isRemote?: boolean;
  /** hours facet values to OR together, e.g. ["Internship", "Contract"] */
  hours?: string[];
  /** free-text appended to the query (software / benefits box) */
  extraKeywords?: string;
  page?: number; // 1-based, converted to Algolia's 0-based
  hitsPerPage?: number;
}

export interface AlgoliaJobSearchResult {
  hits: AlgoliaJobHit[];
  nbHits: number;
  nbPages: number;
  processingTimeMS: number;
  facets: {
    is_remote?: Record<string, number>;
    hours?: Record<string, number>;
  };
  /** what the query parser recognised, for showing back to the reader */
  intent: SearchIntent;
}

/** A dataset page that answers the same query the reader typed. */
export interface AlgoliaDatasetHit {
  objectID: string;
  name: string;
  tagline: string | null;
  url: string;
  group: string | null;
}

export async function searchJobsAlgolia({
  query,
  location,
  isRemote,
  hours,
  extraKeywords,
  page = 1,
  hitsPerPage = 20,
}: AlgoliaJobSearchParams): Promise<AlgoliaJobSearchResult> {
  // Location and extra keywords fold into the text query; city/region/metro are searchable.
  const raw = [query, location, extraKeywords].filter(Boolean).join(" ").trim();
  // Read the query before sending it: "remote" becomes a filter instead of a
  // word, and a recognised role or metro becomes a ranking boost. See
  // searchIntent.ts for why role and metro boost rather than filter.
  const intent = parseSearchIntent(raw);
  const q = intent.text;
  // premium_job = the above-market sweep set. Their url points at /go, which
  // gates the outbound link behind sign-in / email capture — so they can appear
  // in public search without giving the destination away.
  // The explicit Remote toggle and a typed "remote" mean the same thing.
  const wantRemote = isRemote || intent.remote;
  const filters = wantRemote
    ? "(type:job OR type:premium_job) AND is_remote:true"
    : "(type:job OR type:premium_job)";

  // optionalFilters re-rank without restricting: a recognised role floats real
  // Controllers above "Document Controller" while still returning "Assistant
  // Controller", and a recognised metro floats Denver jobs without hiding the
  // 33% of postings that carry no metro at all.
  const optionalFilters = [
    ...(intent.role ? [`role:${intent.role}<score=3>`] : []),
    ...(intent.metro ? [`metro:${intent.metro}<score=2>`] : []),
  ];

  // hours toggles are a single OR group (Internship OR Contract ...)
  const facetFilters =
    hours && hours.length > 0 ? [hours.map((h) => `hours:${h}`)] : undefined;

  const data = await algoliaQuery({
      query: q,
      filters,
      facetFilters,
      ...(optionalFilters.length ? { optionalFilters } : {}),
      facets: ["is_remote", "hours"],
      page: Math.max(0, page - 1),
      hitsPerPage,
      attributesToHighlight: ["title"],
      getRankingInfo: true,
      attributesToRetrieve: [
        "title",
        "company",
        "location",
        "city",
        "region",
        "metro",
        "country",
        "role",
        "seniority",
        "salary",
        "salary_min",
        "salary_max",
        "is_remote",
        "hours",
        "logo",
        "featured",
        "posted",
        "url",
      ],
  });

  return {
    hits: data.hits ?? [],
    nbHits: data.nbHits ?? 0,
    nbPages: data.nbPages ?? 0,
    processingTimeMS: data.processingTimeMS ?? 0,
    facets: data.facets ?? {},
    intent,
  };
}

/**
 * The dataset pages that answer the same question, searched in the same index.
 *
 * Someone typing "controller denver" wants the openings AND what Denver pays a
 * controller; before this, all 83 dataset pages were invisible to search
 * ("layoffs", "public pay ontario" and "cpa exam support" each returned zero).
 */
export async function searchDatasetsAlgolia(query: string, hitsPerPage = 3): Promise<AlgoliaDatasetHit[]> {
  const intent = parseSearchIntent(query);
  const q = intent.text.trim();
  if (!q) return [];
  const data = await algoliaQuery({
    query: q,
    filters: "type:dataset",
    hitsPerPage,
    // There are 140 dataset records against a query written for job postings,
    // so requiring every word (Algolia's default AND) matched nothing useful:
    // "controller salary denver" returned 0 because no single page carries all
    // three words. Falling back to optional words finds the Denver page.
    removeWordsIfNoResults: "allOptional",
    // ...and with words optional, ranking is what decides. A metro the parser
    // actually recognised is a much stronger signal than a word overlap, which
    // is what put "What does the FP&A ladder pay?" above the Denver page for
    // "what does denver pay".
    ...(intent.metro ? { optionalFilters: [`metro:${intent.metro}<score=10>`] } : {}),
    attributesToRetrieve: ["name", "tagline", "url", "group"],
  });
  return data.hits ?? [];
}
